<script setup lang="ts">
interface Props {
  cart?: Record<string, unknown> | boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  cart: false,
  disabled: false,
});

const emit = defineEmits<{
  returnTime: [time: string];
}>();

const showTimes = ref(false);
const chosenTime = ref<string | false>(false);
const buttonRef = ref<HTMLElement | null>(null);
const slotsStyle = ref<Record<string, string>>({});

const cartObj = computed(() =>
  props.cart && typeof props.cart === "object" ? (props.cart as Record<string, unknown>) : null,
);
const dateObj = computed(() =>
  cartObj.value?.date && typeof cartObj.value.date === "object"
    ? (cartObj.value.date as Record<string, unknown>)
    : null,
);

const hasWindows = computed(() => {
  const windows = dateObj.value?.deliveryWindows as { name: string }[] | undefined;
  return windows && windows.length > 0;
});

const hasDate = computed(() => !!dateObj.value?.full);

const hasSelected = computed(() => {
  return dateObj.value?.selectedWindow || cartObj.value?.selectedWindow;
});

// The current cart slot, displayed in the same "HH:MM - HH:MM" form as
// the rows in the dropdown so highlight matching is a simple equality
// check — and stays in sync with cart state on every open (the previous
// local-only `chosenTime` ref drifted as soon as the user closed and
// re-opened the picker).
const currentSlot = computed(() => selectedWindowDisplay.value);

const selectedWindowDisplay = computed(() => {
  // `selectedWindow` is the END of the 30-minute delivery window (this
  // matches old-site and what the backend has historically received as
  // `delivery_date_time` — see [TimePicker.vue](../../old-site/components/TimePicker.vue)
  // and `orders.ts` `delivery_date_time` concat). To render the
  // human-readable range we subtract 30 minutes for the start.
  const sw = dateObj.value?.selectedWindow || cartObj.value?.selectedWindow;
  if (!sw) return null;
  const endStr = String(sw);
  const [h, m] = endStr.split(":").map(Number);
  const startTotalMin = h * 60 + (m || 0) - 30;
  if (startTotalMin < 0) return endStr;
  const sh = String(Math.floor(startTotalMin / 60)).padStart(2, "0");
  const sm = String(startTotalMin % 60).padStart(2, "0");
  return `${sh}:${sm} - ${endStr}`;
});

/**
 * Generate 30-minute window display slots from delivery windows.
 * Each delivery window name (e.g. "09:00") is the START of the window.
 * Display as "09:00 - 09:30".
 */
const times = computed(() => {
  const windows = dateObj.value?.deliveryWindows as { name: string }[] | undefined;

  if (!windows || windows.length === 0) return [];

  return windows.map((w) => {
    const [h, m] = w.name.split(":").map(Number);
    const startMin = h * 60 + (m || 0);
    const endMin = startMin + 30;
    const sh = String(Math.floor(startMin / 60)).padStart(2, "0");
    const sm = String(startMin % 60).padStart(2, "0");
    const eh = String(Math.floor(endMin / 60)).padStart(2, "0");
    const em = String(endMin % 60).padStart(2, "0");
    return `${sh}:${sm} - ${eh}:${em}`;
  });
});

function updateSlotsPosition() {
  if (!buttonRef.value) return;
  const rect = buttonRef.value.getBoundingClientRect();
  slotsStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    zIndex: "10000",
  };
}

function toggleTimes() {
  if (!hasDate.value) return;
  showTimes.value = !showTimes.value;
  if (showTimes.value) {
    nextTick(() => updateSlotsPosition());
  }
}

function selectTime(slot: string) {
  chosenTime.value = slot;
  // Emit the END of the window (e.g. "14:30" from "14:00 - 14:30"). The
  // backend's `delivery_date_time` contract — and old-site's behaviour —
  // treats `selectedWindow` as the end time; the display in
  // `selectedWindowDisplay` derives the start by subtracting 30 minutes.
  const time = slot.slice(8);
  showTimes.value = false;
  emit("returnTime", time);
}

onMounted(() => {
  window.addEventListener("scroll", updateSlotsPosition, true);
  window.addEventListener("resize", updateSlotsPosition);
});

onUnmounted(() => {
  window.removeEventListener("scroll", updateSlotsPosition, true);
  window.removeEventListener("resize", updateSlotsPosition);
});
</script>

<template>
  <div class="time-picker">
    <div
      ref="buttonRef"
      class="time-picker__button"
      :class="[showTimes && 'selected', !hasSelected && 'error', disabled && 'disabled']"
      @click="!disabled && toggleTimes()"
    >
      <div class="time-picker__button--icon">
        <fa
          :icon="['fal', 'clock']"
          class="pink-text icon"
        />
      </div>
      <div class="time-picker__button--text">
        <p class="grey-text">
          Time
          <fa
            :icon="['fal', showTimes ? 'chevron-up' : 'chevron-down']"
            class="small-icon"
          />
        </p>
        <p
          v-if="selectedWindowDisplay"
          class="light-grey-text small-text"
        >
          {{ selectedWindowDisplay }}
        </p>
        <p
          v-else
          class="light-grey-text small-text"
        >
          {{ hasDate ? "Select a time" : "Choose a date first" }}
        </p>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showTimes"
        class="time-picker__overlay"
        @click="showTimes = false"
      />
      <div
        v-if="showTimes"
        class="time-picker__slots"
        :style="slotsStyle"
      >
        <div class="time-picker__slots--times">
          <template v-if="times.length">
            <div
              v-for="slot in times"
              :key="slot"
              class="slots"
              :class="{ 'selected-time': currentSlot === slot || chosenTime === slot }"
              @click="selectTime(slot)"
            >
              <p>{{ slot }}</p>
            </div>
          </template>
          <p
            v-else
            class="light-grey-text"
            style="text-align: center; margin: 1rem 0"
          >
            No time slots available
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss">
.time-picker {
  position: relative;
  margin-right: 1rem;

  @media (max-width: $tablet) {
    margin-right: 0;
  }

  &__button {
    cursor: pointer;
    display: flex;
    justify-content: flex-start;
    border-radius: 0.4rem;
    padding: 0.5rem;
    border: 2px solid transparent;
    min-width: 7rem;
    width: max-content;
    box-sizing: border-box;

    @media (max-width: $tablet) {
      width: 100%;
      min-width: 0;
      margin: 0;
    }

    &--icon {
      display: flex;
      align-items: center;
      margin-right: 0.5rem;
    }

    &--text {
      flex-direction: column;
      align-items: flex-start;

      .grey-text {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      p {
        margin: 0;
      }
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__slots {
    border-radius: 0.4rem;
    border: 2px solid var(--color-grey-3);
    padding: 1rem;
    background-color: var(--color-white-1);
    transition: 0.5s;
    min-width: 12rem;
    width: max-content;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    @media (max-width: $tablet) {
      left: 1rem !important;
      right: 1rem !important;
      min-width: unset !important;
      width: auto !important;
    }

    &--times {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      align-items: center;
      margin: 0;
      max-height: 200px;
      overflow-y: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }

      .slots {
        padding: 0.3rem;
        width: 90%;
        border-radius: 0.4rem;
        cursor: pointer;

        p {
          color: var(--color-grey-1);
          text-align: center;
          margin: 0;
        }

        &:hover {
          background-color: var(--color-pink-1);

          p {
            color: var(--color-white-1);
          }
        }
      }

      .selected-time {
        background-color: var(--color-pink-1);

        p {
          color: var(--color-white-1);
        }
      }
    }
  }

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
  }

  .selected {
    border: 2px solid var(--color-grey-3);
    background-color: var(--color-white-1);
  }

  .error {
    border: 2px solid var(--color-pink-1);
  }
}
</style>
